/**
 * LiveClassRoom — phòng học live toàn màn hình (thiết kế lại 2026-10-01).
 *
 * Trước đây chỉ bọc prefab `VideoConference` trong layout trang thường: thiếu
 * `data-lk-theme` (chat nền trắng, nút điều khiển gần như vô hình), chữ tiếng Anh,
 * bị header/footer AppLayout chèn ép. Giờ tự dựng từ các khối LiveKit:
 *   - Thanh trên: môn + buổi học, trạng thái, đồng hồ, số người, đang ghi hình.
 *   - Video: lưới; có chia sẻ màn hình → màn hình lớn + dải camera nhỏ.
 *   - Khung phải: Trò chuyện (data channel, không lưu DB — chốt 2026-07-28) /
 *     Người tham gia (mic, camera, đang nói).
 *   - Thanh điều khiển: Micro, Camera, Chia sẻ màn hình, khung bên, Rời lớp.
 *
 * Quyền publish/subscribe do server cấp trong JWT `token` (host vs participant —
 * `apps/live/services/livekit_provider.py`), component không tự phân quyền.
 */
import "@livekit/components-styles";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  CarouselLayout,
  ConnectionStateToast,
  DisconnectButton,
  FocusLayout,
  FocusLayoutContainer,
  GridLayout,
  LayoutContextProvider,
  LiveKitRoom,
  ParticipantTile,
  RoomAudioRenderer,
  TrackToggle,
  isTrackReference,
  useChat,
  useParticipants,
  useTracks,
} from "@livekit/components-react";
import { Track, type Participant } from "livekit-client";
import type { LiveClass } from "@/api/live";
import "./liveClassRoom.css";

interface Props {
  serverUrl: string;
  token: string;
  /** Thông tin buổi học cho thanh trên (tuỳ chọn — thiếu thì chỉ hiện phòng). */
  liveClass?: Pick<LiveClass, "title" | "course_name" | "recording_enabled" | "started_at">;
  /** Gọi khi user rời phòng (bấm Rời lớp, đóng tab, lỗi kết nối). */
  onDisconnected?: () => void;
}

export function LiveClassRoom({ serverUrl, token, liveClass, onDisconnected }: Props) {
  return (
    <LiveKitRoom
      serverUrl={serverUrl}
      token={token}
      connect
      audio
      video
      data-lk-theme="default"
      className="sep-live-room"
      onDisconnected={onDisconnected}
      data-testid="live-class-room"
    >
      <LayoutContextProvider>
        <RoomBody liveClass={liveClass} />
      </LayoutContextProvider>
      <RoomAudioRenderer />
      <ConnectionStateToast />
    </LiveKitRoom>
  );
}

type Panel = "chat" | "people" | null;

function RoomBody({ liveClass }: { liveClass?: Props["liveClass"] }) {
  const [panel, setPanel] = useState<Panel>("chat");
  const participants = useParticipants();
  const { chatMessages } = useChat();
  const [seenCount, setSeenCount] = useState(0);
  useEffect(() => {
    if (panel === "chat") setSeenCount(chatMessages.length);
  }, [panel, chatMessages.length]);
  const unread = panel === "chat" ? 0 : Math.max(0, chatMessages.length - seenCount);

  return (
    <div className="sep-live-shell">
      <TopBar liveClass={liveClass} participantCount={participants.length} />
      <div className="sep-live-main">
        <div className="sep-live-stage">
          <Stage />
        </div>
        {panel && (
          <aside className="sep-live-panel">
            <div className="sep-live-panel-tabs">
              <button
                className={panel === "chat" ? "active" : ""}
                onClick={() => setPanel("chat")}
                type="button"
              >
                Trò chuyện
              </button>
              <button
                className={panel === "people" ? "active" : ""}
                onClick={() => setPanel("people")}
                type="button"
              >
                Người tham gia ({participants.length})
              </button>
              <button
                className="sep-live-panel-close"
                onClick={() => setPanel(null)}
                type="button"
                aria-label="Đóng khung bên"
              >
                ✕
              </button>
            </div>
            {panel === "chat" ? <ChatPanel /> : <PeoplePanel participants={participants} />}
          </aside>
        )}
      </div>
      <div className="sep-live-controls">
        <TrackToggle source={Track.Source.Microphone} showIcon>
          Micro
        </TrackToggle>
        <TrackToggle source={Track.Source.Camera} showIcon>
          Camera
        </TrackToggle>
        <TrackToggle source={Track.Source.ScreenShare} showIcon captureOptions={{ audio: true }}>
          Chia sẻ màn hình
        </TrackToggle>
        <button
          className={`lk-button ${panel === "chat" ? "sep-live-on" : ""}`}
          onClick={() => setPanel(panel === "chat" ? null : "chat")}
          type="button"
        >
          💬 Trò chuyện{unread > 0 && <span className="sep-live-badge">{unread}</span>}
        </button>
        <button
          className={`lk-button ${panel === "people" ? "sep-live-on" : ""}`}
          onClick={() => setPanel(panel === "people" ? null : "people")}
          type="button"
        >
          👥 {participants.length}
        </button>
        <DisconnectButton className="sep-live-leave">Rời lớp</DisconnectButton>
      </div>
    </div>
  );
}

function TopBar({
  liveClass,
  participantCount,
}: {
  liveClass?: Props["liveClass"];
  participantCount: number;
}) {
  const elapsed = useElapsed(liveClass?.started_at ?? null);
  return (
    <header className="sep-live-top">
      <div className="sep-live-title">
        {liveClass?.course_name && <span className="sep-live-course">{liveClass.course_name}</span>}
        <strong>{liveClass?.title ?? "Lớp học trực tuyến"}</strong>
      </div>
      <div className="sep-live-meta">
        <span className="sep-live-pill sep-live-pill-live">● Đang diễn ra</span>
        {elapsed && <span className="sep-live-pill">⏱ {elapsed}</span>}
        <span className="sep-live-pill">👥 {participantCount} người</span>
        {liveClass?.recording_enabled && (
          <span className="sep-live-pill sep-live-pill-rec">⏺ Đang ghi hình</span>
        )}
      </div>
    </header>
  );
}

function Stage() {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );
  const screenShare = tracks.find(
    (t) => isTrackReference(t) && t.publication.source === Track.Source.ScreenShare,
  );

  if (screenShare && isTrackReference(screenShare)) {
    const others = tracks.filter((t) => t !== screenShare);
    return (
      <FocusLayoutContainer className="sep-live-focus">
        <CarouselLayout tracks={others}>
          <ParticipantTile />
        </CarouselLayout>
        <FocusLayout trackRef={screenShare} />
      </FocusLayoutContainer>
    );
  }
  return (
    <GridLayout tracks={tracks} className="sep-live-grid">
      <ParticipantTile />
    </GridLayout>
  );
}

function ChatPanel() {
  const { chatMessages, send, isSending } = useChat();
  const [text, setText] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [chatMessages.length]);

  const submit = async () => {
    const msg = text.trim();
    if (!msg) return;
    await send(msg);
    setText("");
  };

  return (
    <div className="sep-live-chat">
      <div className="sep-live-chat-list" ref={listRef}>
        {chatMessages.length === 0 ? (
          <div className="sep-live-empty">
            Chưa có tin nhắn. Tin nhắn chỉ hiển thị trong buổi học, không lưu lại.
          </div>
        ) : (
          chatMessages.map((m) => (
            <div
              key={m.id ?? `${m.timestamp}`}
              className={`sep-live-msg ${m.from?.isLocal ? "mine" : ""}`}
            >
              <div className="sep-live-msg-head">
                <span>{m.from?.isLocal ? "Bạn" : displayName(m.from)}</span>
                <time>
                  {new Date(m.timestamp).toLocaleTimeString("vi-VN", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </time>
              </div>
              <div className="sep-live-msg-body">{m.message}</div>
            </div>
          ))
        )}
      </div>
      <form
        className="sep-live-chat-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Nhập tin nhắn…"
          maxLength={500}
        />
        <button type="submit" className="lk-button" disabled={isSending || !text.trim()}>
          Gửi
        </button>
      </form>
    </div>
  );
}

function PeoplePanel({ participants }: { participants: Participant[] }) {
  const sorted = useMemo(
    () => [...participants].sort((a, b) => Number(b.isLocal) - Number(a.isLocal)),
    [participants],
  );
  return (
    <ul className="sep-live-people">
      {sorted.map((p) => (
        <li key={p.identity} className={p.isSpeaking ? "speaking" : ""}>
          <span className="sep-live-avatar">{displayName(p).charAt(0).toUpperCase()}</span>
          <span className="sep-live-person">
            {displayName(p)}
            {p.isLocal && <em> (Bạn)</em>}
          </span>
          <span title={p.isMicrophoneEnabled ? "Đang bật micro" : "Tắt micro"}>
            {p.isMicrophoneEnabled ? "🎙" : "🔇"}
          </span>
          <span title={p.isCameraEnabled ? "Đang bật camera" : "Tắt camera"}>
            {p.isCameraEnabled ? "📷" : "🚫"}
          </span>
        </li>
      ))}
    </ul>
  );
}

function displayName(p?: Participant): string {
  return p?.name || p?.identity || "Ẩn danh";
}

/** "mm:ss" / "h:mm:ss" từ lúc buổi học bắt đầu. */
function useElapsed(startedAt: string | null): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  if (!startedAt) return null;
  const s = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
  const h = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export default LiveClassRoom;
